import { useEffect, useRef } from 'react'
import {
    GameUIMessageType,
    SetSpeedButtonLevelMessage,
    FeatureStatusChangedMessage,
    FeatureAutostartCounterEndedMessage,
    SplashStatusChangeRequestedMessage,
    ShowInfoLineTextMessage,
    FreespinsStatusChangedMessage,
    BetResponseReceivedMessage,
    BetRequestedMessage,
} from 'game-ui-types'
import { useUltraPlayStore } from '@stores/ultraPlayStore'
import { useConfigurationStore } from '@stores'
import { cancelPendingUltraPlayCollect, collectUltraPlayWin } from './ultraPlayCollect'
import { confirmUltraPlayAutoplayStop, flushPendingUltraPlayPause } from './ultraPlayPause'
import { createUltraPlaySpinRecorder } from './ultraPlaySpinRecorder'
import { ultraPlayAnalytics } from './ultraPlayAnalytics'

// The engine shows this info-line label (a translation key) when the connection drops. It's the only
// signal Ultra Play gets for the lost connection, and it never clears without a reload.
const NO_CONNECTION_INFO_LINE_KEY = 'common.noConnection'

const BET_IN_FLIGHT_TIMEOUT_MS = 5000

// Owns all Ultra Play <-> engine message syncing, extracted from App.tsx so the feature is
// self-contained. Attaches its own window 'message' listener and delegates bet recording / feature
// lifecycle to the spin recorder; the forwarding cases (speed re-assert, intro/splash skips) just
// keep the engine moving while Ultra Play plays through a feature. The listener is only attached
// when the Ultra Play feature flag is enabled.
export const useUltraPlayEngineSync = () => {
    const recorderRef = useRef<ReturnType<typeof createUltraPlaySpinRecorder> | null>(null)
    const recorder = (recorderRef.current ??= createUltraPlaySpinRecorder())
    const status = useUltraPlayStore((s) => s.status)
    const hasUltraPlay = useConfigurationStore((s) => s.hasUltraPlay)
    const isUltraPlaySupported = useConfigurationStore((s) => s.isUltraPlaySupported)
    const betWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Drop any pending bet when a session ends (→ READY) so the next session starts clean.
    useEffect(() => {
        if (status !== 'READY') return

        recorder.resetPending()
        cancelPendingUltraPlayCollect()
    }, [status, recorder])

    useEffect(() => {
        if (!hasUltraPlay || !isUltraPlaySupported) return

        const clearBetWatchdog = () => {
            if (betWatchdogRef.current === null) return

            clearTimeout(betWatchdogRef.current)
            betWatchdogRef.current = null
        }

        const updateBetInFlight = (inFlight: boolean) => {
            clearBetWatchdog()

            const up = useUltraPlayStore.getState()

            if (inFlight && up.status !== 'PLAYING') return

            if (inFlight) {
                // TODO: Probably no longer needed.
                betWatchdogRef.current = setTimeout(() => {
                    betWatchdogRef.current = null
                    useUltraPlayStore.getState().setBetInFlight(false)
                }, BET_IN_FLIGHT_TIMEOUT_MS)
            }

            if (up.isBetInFlight !== inFlight) up.setBetInFlight(inFlight)

            if (!inFlight) flushPendingUltraPlayPause()
        }

        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const type = rawData?.type

            switch (type) {
                // Unified feature lifecycle for Ultra Play (started / completed).
                case GameUIMessageType.FEATURE_STATUS_CHANGED: {
                    recorder.onFeatureStatusChanged((rawData as FeatureStatusChangedMessage).payload)
                    flushPendingUltraPlayPause()

                    break
                }
                case GameUIMessageType.FREESPINS_STATUS_CHANGED: {
                    recorder.onFreespinCount((rawData as FreespinsStatusChangedMessage).payload)

                    break
                }
                case GameUIMessageType.BET_REQUESTED: {
                    recorder.onBetRequested((rawData as BetRequestedMessage).payload)
                    updateBetInFlight(true)

                    break
                }
                // The server's response for a round — the win and the resulting balance, straight from
                // the engine. This is what every recorder row is built from.
                case GameUIMessageType.BET_RESPONSE_RECEIVED: {
                    recorder.onBetResponse((rawData as BetResponseReceivedMessage).payload)
                    // The round's result is in — Pause is safe again.
                    updateBetInFlight(false)

                    break
                }
                // The engine lost its connection — surface it in Ultra Play, stop any running session
                // (so no more bets are attempted) and keep it blocked until a reload.
                case GameUIMessageType.SHOW_INFO_LINE_TEXT: {
                    if ((rawData as ShowInfoLineTextMessage).text !== NO_CONNECTION_INFO_LINE_KEY) break

                    const up = useUltraPlayStore.getState()

                    if (up.isNoConnection) break

                    up.setNoConnection(true)
                    recorder.resetPending()
                    // Nothing will ever report an outcome now — don't leave the watchdog to do it.
                    updateBetInFlight(false)

                    ultraPlayAnalytics.noConnection()

                    if (up.status === 'PLAYING' || up.status === 'PAUSED') {
                        ultraPlayAnalytics.sessionEnded('no_connection')
                        up.stop()
                    }

                    break
                }
                case GameUIMessageType.AUTOPLAY_BUTTON_CLICKED: {
                    recorder.onAutoplayIssued()

                    break
                }
                case GameUIMessageType.SPIN_BUTTON_UPDATE_AUTOPLAY_COUNT: {
                    recorder.onAutoplayCount((rawData as { payload?: number }).payload)

                    break
                }
                case GameUIMessageType.SPIN_BUTTON_AUTOPLAY_STOPPED: {
                    confirmUltraPlayAutoplayStop()
                    recorder.onAutoplayStopped()

                    break
                }
                // The button is idle again (nothing to collect) or the animations wrapped up — the spin
                // cycle is over, so release Pause.
                case GameUIMessageType.SPIN_BUTTON_SEND_IDLE_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_IDLE_WITHOUT_GAMBLE_STATE:
                case GameUIMessageType.PLAYER_INFO_COMPLETE_ANIMATIONS: {
                    updateBetInFlight(false)

                    break
                }
                case GameUIMessageType.SPIN_BUTTON_SEND_COLLECT_STATE: {
                    const up = useUltraPlayStore.getState()

                    // The reels have stopped — this bet's result is known, so Pause is safe again.
                    updateBetInFlight(false)

                    if (!up.isOpen) {
                        break
                    }

                    // We are probably exiting autoplay because a feature is triggered.
                    // We need to speed up the gameplay by collecting the current win
                    // and starting the feature immediately.
                    // TODO: Test how this behaves in features other than FS
                    collectUltraPlayWin()

                    break
                }
                case GameUIMessageType.SET_SPEED_BUTTON_LEVEL: {
                    // The engine drops the speed to the first level when a feature starts. During Ultra
                    // Play features we want to stay at max speed — re-assert it.
                    const up = useUltraPlayStore.getState()

                    if (up.status !== 'PLAYING') break

                    const maxSpeedLevel = useConfigurationStore.getState().shouldEnableUltraSpeed ? 3 : 2
                    const { speedLevel } = rawData as SetSpeedButtonLevelMessage

                    if (speedLevel < maxSpeedLevel) {
                        window.uiBus.postMessage({
                            type: GameUIMessageType.SET_SPEED_BUTTON_LEVEL,
                            speedLevel: maxSpeedLevel,
                        } as SetSpeedButtonLevelMessage)
                    }

                    break
                }
                case GameUIMessageType.FEATURE_AUTOSTART_COUNTER_STARTED: {
                    // Free spins start screen began — end its counter so Ultra Play doesn't wait on it.
                    // Play-through only: PLAYING and not Stop on Feature (with Stop on Feature we close
                    // Ultra Play and let the game play the feature with its intro). When PAUSED the
                    // Special Feature dialog is up.
                    const up = useUltraPlayStore.getState()
                    const { feature } = (rawData as FeatureAutostartCounterEndedMessage).payload

                    if (up.status === 'PLAYING' && !up.stopOnFeature && feature === 'freespins') {
                        window.uiBus.postMessage({
                            type: GameUIMessageType.FEATURE_AUTOSTART_COUNTER_ENDED,
                            payload: { feature },
                        } as FeatureAutostartCounterEndedMessage)
                    }

                    break
                }
                case GameUIMessageType.FREESPINS_SPLASH_STARTED: {
                    // A free spins splash (intro / retrigger / outro) began — complete it so Ultra Play
                    // skips the animation. Play-through only: PLAYING and not Stop on Feature (with Stop
                    // on Feature the feature plays in the game, so its intro splash must NOT be skipped).
                    const up = useUltraPlayStore.getState()

                    if (up.status === 'PLAYING' && !up.stopOnFeature) {
                        window.uiBus.postMessage({
                            type: GameUIMessageType.SPLASH_STATUS_CHANGE_REQUESTED,
                            payload: { status: 'completed' },
                        } as SplashStatusChangeRequestedMessage)
                    }

                    break
                }
            }
        }

        window.uiBus.addEventListener('message', handleMessage, false)

        return () => {
            window.uiBus.removeEventListener('message', handleMessage, false)
            clearBetWatchdog()
        }
    }, [recorder, hasUltraPlay, isUltraPlaySupported])
}
